type ProductListEntry = {
    zone_sf_id: string
    product_list?: Array<{
        sf_id: string
        image_url?: string
    }>
}

type RawGoalItem = {
    season_active?: boolean
    delivered?: boolean
    finished?: boolean
    scheduled_delivery_date?: string | null
    delivery_date?: string | null
    price: number
    package_id?: string
    product: {
        id: string
        name: string
    }
}

export type ProcessedGoalItem = {
    productId: string
    productName: string
    price: number
    imageUrl: string
    nbOfGoalItems: number
    scheduled_delivery_date?: string | null
    delivery_date?: string | null
    delivered: boolean
    finished: boolean
}

export const NO_DATE = 'no-date'

export type SectionDateGoalItem = {
    delivered: boolean
    delivery_date?: string | null
    scheduled_delivery_date?: string | null
}

/**
 * Banner / tile date bucket. Delivered prefers actual delivery_date;
 * unfinished and finished use scheduled only.
 */
export const getSectionDateKey = (goalItem: SectionDateGoalItem): string => {
    if (goalItem.delivered) {
        return (
            goalItem.delivery_date ||
            goalItem.scheduled_delivery_date ||
            NO_DATE
        )
    }
    return goalItem.scheduled_delivery_date || NO_DATE
}

export const findProduct = (
    productListData: ProductListEntry[],
    zoneSFID: string,
    productId: string
) => {
    const zoneProductList = productListData.find(
        (product) => product.zone_sf_id === zoneSFID
    )

    return zoneProductList?.product_list?.find(
        (product) => product.sf_id === productId
    )
}

export const processVillageClientGoalItems = (
    productListData: ProductListEntry[],
    zoneSFID: string,
    goalItems: RawGoalItem[] = []
): ProcessedGoalItem[] => {
    const goalItemsMap = new Map<string, ProcessedGoalItem>()

    goalItems.forEach((goalItem) => {
        if (goalItem.season_active === false) return

        // After modification delivered is undefined — coerce to boolean
        const delivered = goalItem.delivered === true
        const finished = goalItem.finished === true
        const dateKey = getSectionDateKey({
            delivered,
            delivery_date: goalItem.delivery_date,
            scheduled_delivery_date: goalItem.scheduled_delivery_date
        })
        // When package_id is available it uniquely identifies the size, so price
        // is excluded from the key — different net prices under a discount cap
        // should still merge into one tile. When package_id is absent (legacy
        // data), price is the only size discriminator: two sizes of the same
        // product have different list prices, so including price here prevents
        // them from collapsing into one tile with an inflated count and sum.
        const packageOrProduct = goalItem.package_id
            ? goalItem.package_id
            : `${goalItem.product.id}-${goalItem.price}`
        const key = `${packageOrProduct}-${delivered}-${finished}-${dateKey}`
        const existingGoalItem = goalItemsMap.get(key)

        const product = findProduct(
            productListData,
            zoneSFID,
            goalItem.product.id
        )
        const imageUrl =
            product?.image_url &&
            /\.(png|jpg|jpeg|svg)$/i.test(product.image_url)
                ? product.image_url
                : ''

        const rowPrice = Number(goalItem.price) || 0

        if (existingGoalItem) {
            goalItemsMap.set(key, {
                ...existingGoalItem,
                price: existingGoalItem.price + rowPrice,
                nbOfGoalItems: existingGoalItem.nbOfGoalItems + 1
            })
        } else {
            goalItemsMap.set(key, {
                productId: goalItem.product.id,
                productName: goalItem.product.name,
                price: rowPrice,
                imageUrl,
                nbOfGoalItems: 1,
                scheduled_delivery_date: goalItem.scheduled_delivery_date,
                delivery_date: goalItem.delivery_date,
                delivered,
                finished
            })
        }
    })

    return Array.from(goalItemsMap.values())
}
